// src/pages/ProjectDetail/ProjectDetail.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import "./ProjectDetail.css";

import { getProjectByKey, isVideoSrc, resolveGcsMedia } from "../../data/projectsData";

/* ---------------- Active section hook (no ESLint warnings) ---------------- */
function useActiveSection(ids = []) {
  const idsKey = useMemo(() => (Array.isArray(ids) ? ids.join("|") : ""), [ids]);

  const [active, setActive] = useState(() => (idsKey ? idsKey.split("|")[0] : ""));

  useEffect(() => {
    setActive(idsKey ? idsKey.split("|")[0] : "");
  }, [idsKey]);

  useEffect(() => {
    const list = idsKey ? idsKey.split("|") : [];
    const els = list.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio || 0) - (a.intersectionRatio || 0))[0];

        if (visible?.target?.id) setActive(visible.target.id);
      },
      { root: null, rootMargin: "-20% 0px -68% 0px", threshold: [0.05, 0.15, 0.3] }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [idsKey]);

  return active;
}

export default function ProjectDetail() {
  const { key } = useParams();
  const nav = useNavigate();

  const project = useMemo(() => getProjectByKey(key), [key]);

  const videoRef = useRef(null);

  const sections = useMemo(
    () => [
      { id: "overview", label: "Overview" },
      { id: "whoFor", label: "Who it’s for" },
      { id: "flow", label: "How it works" },
      { id: "features", label: "Key Features" },
    //   { id: "architecture", label: "Architecture" },
      { id: "results", label: "Results" },
    ],
    []
  );

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);
  const active = useActiveSection(sectionIds);

  // Safe media compute (even if project is null)
  const src = useMemo(() => {
    if (!project) return "";
    return resolveGcsMedia(project.media);
  }, [project]);

  const vid = useMemo(() => {
    if (!src) return false;
    return isVideoSrc(src);
  }, [src]);

  const story = useMemo(() => project?.story || {}, [project]);

  // header-aware smooth scroll (uses CSS var --siteHeaderOffset)
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (!el) return;

    const headerOffset =
      parseInt(getComputedStyle(document.documentElement).getPropertyValue("--siteHeaderOffset")) || 92;

    const y = el.getBoundingClientRect().top + window.scrollY - headerOffset;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // ✅ Always-called hook (not conditional) — only plays when a video exists
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (!vid || !src) return;

    try {
      v.muted = true;
      v.playsInline = true;
      v.preload = "auto";
      v.currentTime = 0;

      const p = v.play?.();
      if (p?.catch) p.catch(() => {});
    } catch {}
  }, [key, vid, src]);

  // Now it's safe to early-return (hooks already executed above)
  if (!project) {
    return (
      <main className="pdet">
        <div className="pdet__shell">
          <div className="pdet__topbar">
            <button className="pdet__ghost" onClick={() => nav(-1)}>
              ← Back
            </button>
            <Link className="pdet__ghost" to="/projects" onClick={() => window.scrollTo(0, 0)}>
              All projects
            </Link>
          </div>

          <h1 className="pdet__title">Project not found</h1>
          <p className="pdet__muted">That link doesn’t match an existing project.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="pdet" aria-label={`${project.title} details`}>
      <div className="pdet__shell">
        {/* Top bar */}
        <div className="pdet__topbar">
          <Link className="pdet__ghost" to="/projects" onClick={() => window.scrollTo(0, 0)}>
            ← All projects
          </Link>
          <Link className="pdet__ghost" to="/" onClick={() => window.scrollTo(0, 0)}>
            Home
          </Link>
        </div>

        {/* Hero */}
        <header className="pdet__hero">
          <div className="pdet__heroGrid">
            <div className="pdet__heroLeft">
              <div className="pdet__kicker">{project.tag}</div>
              <h1 className="pdet__title">{project.title}</h1>
              <p className="pdet__sub">{project.subtitle}</p>

              <div className="pdet__metaLine">
                <span className="pdet__metaLabel">Audience</span>
                <span className="pdet__metaValue">{project.audience}</span>
              </div>

              <p className="pdet__desc">{story.summary || project.desc}</p>

              <div className="pdet__stack" aria-label="Tech stack">
                {project.stack.map((s) => (
                  <span key={s} className="pdet__chip">
                    {s}
                  </span>
                ))}
              </div>

              <div className="pdet__jump">
                <button className="pdet__jumpBtn" onClick={() => scrollTo("flow")}>
                  See how it works ↓
                </button>
              </div>
            </div>

            <div className="pdet__heroRight" aria-label="Project preview">
              <div className="pdet__media">
                {vid ? (
                  <video
                    ref={videoRef}
                    className="pdet__video"
                    src={src}
                    muted
                    loop
                    playsInline
                    preload="auto"
                    autoPlay
                  />
                ) : (
                  <img className="pdet__img" src={src} alt={project.title} loading="lazy" />
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content grid: sticky nav + sections */}
        <div className="pdet__grid">
          <aside className="pdet__side" aria-label="On this page">
            <div className="pdet__sideInner">
              <div className="pdet__sideTitle">On this page</div>
              <nav className="pdet__nav">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    className={`pdet__navItem ${active === s.id ? "isActive" : ""}`}
                    onClick={() => scrollTo(s.id)}
                  >
                    <span className="pdet__dot" />
                    {s.label}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          <section className="pdet__main">
            <div id="overview" className="pdet__section">
              <h2 className="pdet__h2">Overview</h2>
              <p className="pdet__p">{project.desc}</p>

              {!!project.highlights?.length && (
                <ul className="pdet__bullets">
                  {project.highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              )}
            </div>

            <div id="whoFor" className="pdet__section">
              <h2 className="pdet__h2">Who it’s for</h2>
              <ul className="pdet__bullets">
                {(story.whoFor || []).map((u) => (
                  <li key={u}>{u}</li>
                ))}
              </ul>
            </div>

            <div id="flow" className="pdet__section">
              <h2 className="pdet__h2">How it works</h2>
              <div className="pdet__steps" role="list">
                {(story.flow || []).map((w, i) => (
                  <div key={w.title} className="pdet__step" role="listitem">
                    <div className="pdet__stepIdx">{String(i + 1).padStart(2, "0")}</div>
                    <div>
                      <div className="pdet__stepTitle">{w.title}</div>
                      <div className="pdet__stepDetail">{w.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div id="features" className="pdet__section">
              <h2 className="pdet__h2">Key Features</h2>
              <ul className="pdet__bullets">
                {(story.features || []).map((h) => (
                  <li key={h}>{h}</li>
                ))}
              </ul>
            </div>

            {/* <div id="architecture" className="pdet__section">
              <h2 className="pdet__h2">Architecture</h2>
              <ul className="pdet__bullets">
                {(story.architecture || []).map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </div> */}

            <div id="results" className="pdet__section">
              <h2 className="pdet__h2">Results</h2>
              <ul className="pdet__bullets">
                {(story.results || []).map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>

              <div className="pdet__footerLinks">
                <Link className="pdet__underline" to="/projects" onClick={() => window.scrollTo(0, 0)}>
                  Back to all projects →
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
